import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { clientConfig } from '@/config'
import {
  setTokens as setApiTokens,
  clearTokens as clearApiTokens,
  setMemoryOnlyAccessToken,
  enableMemoryOnlyTokenStorage,
  API_BASE,
} from '@/services/api'
import {
  isDesktopRuntime,
  loginWithDesktop as startDesktopLogin,
  logoutDesktop,
  onDesktopDeviceRevoked,
  restoreDesktopSession,
} from '@/services/desktopAuth'
import { logger } from '@/utils/logger'

// Storage key helpers with namespace prefix
const getStorageKey = (key: keyof typeof clientConfig.storage.keys): string => {
  return `${clientConfig.storage.keyPrefix}${clientConfig.storage.keys[key]}`
}

interface User {
  id: string
  email: string
  name: string
  picture?: string
  organization_id?: string
  isSuperAdmin?: boolean
}

export type AuthError = Error & {
  code?: string
  status?: number
  email?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  loginWithGoogle: (googleToken: string) => Promise<void>
  loginWithPassword: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  loginWithDesktop: () => Promise<void>
  verifyEmailCode: (email: string, code: string) => Promise<void>
  establishSession: (response: Record<string, unknown>) => void
  logout: () => Promise<void>
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

const parseResponseDetail = (payload: unknown): string => {
  if (!payload || typeof payload !== 'object') {
    return ''
  }
  const maybeDetail = (payload as Record<string, unknown>).detail
  if (typeof maybeDetail === 'string') {
    return maybeDetail
  }
  const maybeMessage = (payload as Record<string, unknown>).message
  if (typeof maybeMessage === 'string') {
    return maybeMessage
  }
  return ''
}

const safePasswordLoginError = (status: number, detail: string): string => {
  const detailLower = detail.toLowerCase()
  if (status === 401) {
    return 'Invalid email or password.'
  }
  if (status === 403 && detailLower.includes('verify')) {
    return 'Email not verified. Enter the 6-digit code we sent to continue.'
  }
  if (status === 429) {
    return 'Too many sign-in attempts. Please try again in a few minutes.'
  }
  if (status >= 500) {
    return "We couldn't sign you in right now. Please try again."
  }
  return "We couldn't sign you in. Please check your details and try again."
}

const safeGoogleLoginError = (status: number): string => {
  if (status === 404) {
    return 'Google sign-in failed. Please try again.'
  }
  if (status === 429) {
    return 'Too many sign-in attempts. Please try again in a few minutes.'
  }
  if (status >= 500) {
    return 'Google sign-in is temporarily unavailable. Please try again.'
  }
  return 'Google sign-in failed. Please try again.'
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const desktopAuthorityGeneration = useRef(0)

  const establishSession = useCallback((data: Record<string, unknown>) => {
    // API may nest payload under "data"
    const payload = (data?.data || data) as Record<string, unknown>
    const userObj = payload?.user as Record<string, unknown> | undefined
    const authToken = (payload?.accessToken || payload?.access_token || payload?.token) as string | undefined
    const email = (userObj?.email || payload?.email) as string | undefined
    const firstName = (userObj?.firstName || userObj?.first_name || payload?.firstName || payload?.first_name) as string | undefined
    const lastName = (userObj?.lastName || userObj?.last_name || payload?.lastName || payload?.last_name) as string | undefined
    const suppliedName = (userObj?.name || payload?.name) as string | undefined
    const userData: User = {
      id: (userObj?.id || payload?.id) as string,
      email: email as string,
      name: suppliedName || `${firstName ?? ''} ${lastName ?? ''}`.trim() || email || '',
      picture: (userObj?.picture || payload?.picture) as string | undefined,
      organization_id: (userObj?.organization_id || userObj?.tenant_id || payload?.organization_id || payload?.tenant_id) as string | undefined,
      isSuperAdmin: (userObj?.isSuperAdmin || payload?.isSuperAdmin) as boolean | undefined,
    }

    if (!authToken || !userData.id || !userData.email) {
      throw new Error('Authentication response missing required fields')
    }

    setToken(authToken)
    setUser(userData)
    if (isDesktopRuntime()) {
      setMemoryOnlyAccessToken(authToken)
      localStorage.removeItem(getStorageKey('authUser'))
    } else {
      // Sync both the api.ts in-memory cache AND localStorage atomically.
      setApiTokens(authToken)
      localStorage.setItem(getStorageKey('authUser'), JSON.stringify(userData))
    }
  }, [])

  // Restore from the native Keychain on desktop; use browser storage on web.
  useEffect(() => {
    if (isDesktopRuntime()) {
      let disposed = false
      let unlisten: (() => void) | undefined
      enableMemoryOnlyTokenStorage()
      localStorage.removeItem(getStorageKey('authUser'))
      void onDesktopDeviceRevoked((revocation) => {
        desktopAuthorityGeneration.current += 1
        setUser(null)
        setToken(null)
        clearApiTokens()
        enableMemoryOnlyTokenStorage()
        localStorage.removeItem(getStorageKey('authUser'))
        setError(revocation.remediationRequired || !revocation.localStateRemoved
          ? 'This device was removed. Ricald could not remove all protected local data; quit and reopen Desktop, then try again.'
          : 'This device was removed and its protected local data was cleared.')
      }).then((disposeListener) => {
        if (disposed) disposeListener()
        else unlisten = disposeListener
      }).catch((listenerError) => {
        logger.error('Desktop revocation listener failed', listenerError)
      })
      const restoreAuthorityGeneration = desktopAuthorityGeneration.current
      void restoreDesktopSession()
        .then((session) => {
          if (
            session
            && !disposed
            && restoreAuthorityGeneration === desktopAuthorityGeneration.current
          ) {
            establishSession(session as unknown as Record<string, unknown>)
          }
        })
        .catch((restoreError) => {
          logger.error('Desktop session restore failed', restoreError)
          setError('Ricald could not restore the desktop session.')
        })
        .finally(() => setIsLoading(false))
      return () => {
        disposed = true
        unlisten?.()
      }
    }

    const storedToken = localStorage.getItem(getStorageKey('authToken'))
    const storedUser = localStorage.getItem(getStorageKey('authUser'))

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser) as User
        if (!parsedUser?.id || !parsedUser?.email) {
          throw new Error('Stored user is incomplete')
        }
        setToken(storedToken)
        setApiTokens(storedToken)
        setUser(parsedUser)
      } catch {
        clearApiTokens()
        localStorage.removeItem(getStorageKey('authUser'))
      }
    } else if (storedToken || storedUser) {
      clearApiTokens()
      localStorage.removeItem(getStorageKey('authUser'))
    }
    setIsLoading(false)
  }, [establishSession])

  const loginWithDesktop = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    const loginAuthorityGeneration = desktopAuthorityGeneration.current
    try {
      const session = await startDesktopLogin()
      if (loginAuthorityGeneration !== desktopAuthorityGeneration.current) {
        throw new Error('This device was removed during sign-in.')
      }
      establishSession(session as unknown as Record<string, unknown>)
    } catch (loginError) {
      const message = loginError instanceof Error
        ? loginError.message
        : typeof loginError === 'string' ? loginError : 'Desktop sign-in failed'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }, [establishSession])

  const loginWithGoogle = useCallback(async (googleToken: string) => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${API_BASE}/auth/google-login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: googleToken }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const detail = parseResponseDetail(errorData)
        throw new Error(safeGoogleLoginError(response.status) || detail || 'Google sign-in failed')
      }

      const data = await response.json()
      establishSession(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed'
      setError(message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [establishSession])

  const loginWithPassword = useCallback(
    async (email: string, password: string, rememberMe = false) => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/auth/login`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password, rememberMe }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          const detail = parseResponseDetail(errorData)
          const authError: AuthError = new Error(safePasswordLoginError(response.status, detail))
          authError.status = response.status
          if (response.status === 403 && detail.toLowerCase().includes('verify')) {
            authError.code = 'EMAIL_NOT_VERIFIED'
            authError.email = email
          }
          throw authError
        }

        const data = await response.json()
        establishSession(data)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Login failed'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [establishSession]
  )

  const verifyEmailCode = useCallback(
    async (email: string, code: string) => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_BASE}/auth/verify-email`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, code }),
        })

        const data = await response.json().catch(() => ({}))

        if (!response.ok) {
          const detail = data?.detail || data?.message || 'Verification failed'
          const verifyError: AuthError = new Error(detail)
          verifyError.status = response.status
          verifyError.code = response.status === 400 && detail.toLowerCase().includes('expired') ? 'CODE_EXPIRED' : 'VERIFICATION_FAILED'
          verifyError.email = email
          throw verifyError
        }

        establishSession(data)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Verification failed'
        setError(message)
        throw err
      } finally {
        setIsLoading(false)
      }
    },
    [establishSession]
  )

  const logout = useCallback(async () => {
    if (isDesktopRuntime()) {
      let remoteRevocationUnconfirmed = false
      try {
        const outcome = await logoutDesktop()
        if (!outcome.localStateRemoved) {
          throw new Error('Desktop local state was not removed')
        }
        remoteRevocationUnconfirmed = outcome.remoteSessionRevocation === 'unconfirmed'
      } catch (logoutError) {
        logger.error('Desktop logout request failed', logoutError)
        setError('Ricald could not remove the desktop session from this Mac. Try signing out again.')
        throw logoutError
      }
      setUser(null)
      setToken(null)
      clearApiTokens()
      enableMemoryOnlyTokenStorage()
      localStorage.removeItem(getStorageKey('authUser'))
      setError(remoteRevocationUnconfirmed
        ? 'Signed out on this Mac. Ricald could not confirm that the remote device session was revoked.'
        : null)
      return
    }
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    } catch (err) {
      logger.error('Logout request failed', err)
      // Continue clearing local state even if the API call fails
    } finally {
      setUser(null)
      setToken(null)
      setError(null)
      clearApiTokens()
      localStorage.removeItem(getStorageKey('authUser'))
    }
  }, [])

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    loginWithGoogle,
    loginWithPassword,
    loginWithDesktop,
    verifyEmailCode,
    establishSession,
    logout,
    error,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
